import Editor, { type Monaco } from "@monaco-editor/react";

interface StrategyEditorProps {
  value: string;
  onChange: (code: string) => void;
}

const TA_COMPLETIONS = [
  { label: "ema", detail: "ta.ema(history, period) → number[]" },
  { label: "sma", detail: "ta.sma(history, period) → number[]" },
  { label: "rsi", detail: "ta.rsi(history, period=14) → number[]" },
  { label: "atr", detail: "ta.atr(history, period=14) → number[]" },
  { label: "macd", detail: "ta.macd(history) → {macd,signal,histogram}[]" },
  { label: "bollinger", detail: "ta.bollinger(history, period, stdDev)" },
  { label: "last", detail: "ta.last(array) → T | null" },
];

const SMC_COMPLETIONS = [
  { label: "orderBlock", detail: "smc.orderBlock(history) → OrderBlock | null" },
  { label: "fvg", detail: "smc.fvg(history) → FairValueGap | null" },
  { label: "swingHighs", detail: "smc.swingHighs(history, lookback)" },
  { label: "swingLows", detail: "smc.swingLows(history, lookback)" },
];

function beforeMount(monaco: Monaco) {
  type CompletionProvider = NonNullable<
    Parameters<Monaco["languages"]["registerCompletionItemProvider"]>[1]
  >;
  type ProvideItems = NonNullable<CompletionProvider["provideCompletionItems"]>;
  type CompletionKind = Monaco["languages"]["CompletionItemKind"];

  monaco.languages.registerCompletionItemProvider("javascript", {
    triggerCharacters: ["."],
    provideCompletionItems: (
      model: Parameters<ProvideItems>[0],
      position: Parameters<ProvideItems>[1]
    ) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      const linePrefix = model
        .getLineContent(position.lineNumber)
        .substring(0, position.column - 1);

      const toSuggestion = (items: typeof TA_COMPLETIONS, kind: CompletionKind) =>
        items.map((item) => ({
          label: item.label,
          kind,
          insertText: item.label,
          detail: item.detail,
          range,
        }));

      if (/\bta\.$/.test(linePrefix)) {
        return { suggestions: toSuggestion(TA_COMPLETIONS, monaco.languages.CompletionItemKind.Method) };
      }
      if (/\bsmc\.$/.test(linePrefix)) {
        return { suggestions: toSuggestion(SMC_COMPLETIONS, monaco.languages.CompletionItemKind.Method) };
      }
      return { suggestions: [] };
    },
  });
}

export default function StrategyEditor({ value, onChange }: StrategyEditorProps) {
  return (
    <Editor
      height="100%"
      defaultLanguage="javascript"
      defaultValue={value}
      value={value}
      theme="vs-dark"
      beforeMount={beforeMount}
      onChange={(v) => onChange(v ?? "")}
      options={{
        fontSize: 13,
        fontFamily: "JetBrains Mono, ui-monospace, SFMono-Regular, monospace",
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        padding: { top: 12, bottom: 12 },
        tabSize: 2,
        renderLineHighlight: "line",
        scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
        wordWrap: "on",
        lineNumbers: "on",
        folding: true,
      }}
    />
  );
}
